// 2026-04-24 (rewrite): Admin Module Editor — single-page xen kẽ
//
// Layout: 1 cột duy nhất, scroll dọc. Cấu trúc:
//   [Chương 1: title + body_md textarea + Q&A list inline]
//   [Chương 2: title + body_md textarea + Q&A list inline]
//   ...
//   [+ Thêm chương]
//
// Không có sidebar, không có modal. Auto-save debounced 1s.
// Admin gõ tay 100% (không upload file).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
// M146 (2026-05-12): manager với quyền cũng được vào page
import { hasPermission } from '../lib/permissions.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
import AdminTotpReverifyModal from '../components/AdminTotpReverifyModal.jsx';
import AdminPageHeader from '../components/AdminPageHeader.jsx';
import RichTextEditor from '../components/RichTextEditor.jsx';
// M82 (2026-04-26): Xoá import stripHtml, toPlainPreview — không còn dùng sau khi xoá QuestionEditor.

// M43: Badge + nút "Embed" nhỏ dùng chung cho cả module + question
// M105 (2026-04-26): cho phép re-embed cả khi state='ok' (admin có thể chủ động re-embed riêng module).
function EmbedBadge({ state, onEmbed, size = 'sm', title = '' }) {
  // state: 'ok' | 'stale' | 'missing'
  const cls = {
    ok:      'bg-emerald-100 text-emerald-700 border-emerald-200',
    stale:   'bg-amber-100 text-amber-700 border-amber-200',
    missing: 'bg-rose-100 text-rose-700 border-rose-200',
  }[state] || 'bg-slate-100 text-slate-600 border-slate-200';
  const icon = { ok: '✅', stale: '🔄', missing: '⚠️' }[state] || '❔';
  const label = { ok: 'Đã embed', stale: 'Cần cập nhật', missing: 'Chưa embed' }[state] || state;
  const tip = {
    ok:      'Đã embed — bấm nút "🔄" để re-embed lại nếu muốn (hữu ích khi vừa đổi setting chunk)',
    stale:   'Đã embed trước đây nhưng nội dung đã đổi — cần embed lại',
    missing: 'Chưa embed — bấm nút Embed để tạo vector',
  }[state] || '';
  // Button label + style theo state
  const btnText = state === 'ok' ? '🔄' : 'Embed';
  const btnTitle = state === 'ok' ? 'Re-embed lại chương này (vd sau khi đổi setting chunk)' : 'Embed ngay';
  const btnCls = state === 'ok'
    ? 'text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
    : 'text-[10px] px-2 py-0.5 rounded bg-sky-600 hover:bg-sky-700 text-white font-semibold shadow-sm';
  return (
    <span className="inline-flex items-center gap-1" title={title || tip}>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${cls}`}>
        <span>{icon}</span>
        <span>{label}</span>
      </span>
      {onEmbed && (
        <button onClick={onEmbed} className={btnCls} title={btnTitle}>
          {btnText}
        </button>
      )}
    </span>
  );
}

// M82 (2026-04-26): Xoá QuestionEditor — không còn nơi nào dùng. Admin không
// quản Q&A trong trang này nữa (M80 thay thế bằng AI sinh on-demand cho NV).

// ==== Chương editor (1 module) ====
function ChapterBlock({ m, index, total, onReload, onMove, onAfterEmbed }) {
  const [title, setTitle] = useState(m.title || '');
  const [bodyMd, setBodyMd] = useState(m.body_md || '');
  const [saving, setSaving] = useState(false);
  const [embedModBusy, setEmbedModBusy] = useState(false);  // M43
  // M77 (2026-04-25): local override embed state — sau khi embed module thành
  // công, set true để badge hiện "ok" ngay mà KHÔNG cần parent loadModules()
  // (gây thunder herd cùng class với M76). Reset khi m.id đổi (chương khác).
  const [localModuleEmbedOk, setLocalModuleEmbedOk] = useState(false);
  // M67 (2026-04-25): collapse/expand chương — default expanded
  const [collapsed, setCollapsed] = useState(false);
  // M81 (2026-04-26): bỏ auto-save → manual save với dirty state
  const isDirty = title !== (m.title || '') || bodyMd !== (m.body_md || '');

  // M82 (2026-04-26): manual save thay auto-save. Hiện nút "💾 Lưu" khi dirty.
  async function saveChanges() {
    if (saving) return;
    if (title.trim().length < 2) {
      toast.error('Tiêu đề chương phải ≥ 2 ký tự');
      return;
    }
    setSaving(true);
    try {
      await api.adminRulesModuleUpdate(m.id, {
        title: title.trim(),
        body_md: bodyMd,
      });
      // Mutate m để dirty re-tính = false (không reload toàn trang)
      m.title = title.trim();
      m.body_md = bodyMd;
      toast.success('Đã lưu chương');
    } catch (e) {
      toast.error('Lưu chương lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setSaving(false);
    }
  }

  function onTitleChange(v) { setTitle(v); }
  function onBodyChange(v) { setBodyMd(v); }

  // M81 + M82 (2026-04-26): Bỏ hoàn toàn quản lý Q&A trong trang này.
  // M80 thay thế: NV bấm "Kiểm tra" → AI sinh câu hỏi on-demand mỗi lần.
  // Admin chỉ quản lý nội dung body chương; câu hỏi NV xem dưới /admin/rules-m80.
  // Data cũ trong rules_test_questions vẫn còn DB (cho rules_exam M9 định kỳ
  // nếu admin muốn dùng), nhưng KHÔNG hiển thị ở đây nữa.

  async function deleteChapter() {
    const ok = await showConfirm({
      title: 'Xoá chương',
      message: `Xoá chương "${title}"?\n\nTất cả tiến độ học M80 (sets, attempts, peek log) liên quan chương này sẽ bị xoá theo.`,
      confirmText: 'Xoá chương', dangerous: true,
    });
    if (!ok) return;
    try {
      await api.adminRulesModuleDelete(m.id);
      toast.success('Đã xoá chương');
      onReload?.();
    } catch (e) {
      toast.error('Xoá chương lỗi: ' + (e?.data?.error || e?.message));
    }
  }

  return (
    <section
      id={`chapter-${m.id}`}
      className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden scroll-mt-20 hover:shadow-md transition-shadow"
    >
      {/* M67 (2026-04-25): Header chương redesign — gọn, gradient nhẹ, badge số to */}
      <div className="bg-gradient-to-r from-pink-50 via-white to-emerald-50 border-b border-slate-200">
        {/* Hàng 1: Badge số + Title input full-width */}
        <div className="px-5 pt-4 pb-2 flex items-start gap-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Mở rộng chương' : 'Thu gọn chương'}
            className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-emerald-500 text-white text-base font-bold flex items-center justify-center shadow-sm hover:shadow-md transition relative"
          >
            <span>{index + 1}</span>
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white text-slate-600 text-[10px] flex items-center justify-center border border-slate-300">
              {collapsed ? '▶' : '▼'}
            </span>
          </button>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={title}
              onChange={e => onTitleChange(e.target.value)}
              placeholder="Tên chương (VD: Chương 1 — An toàn cháy nổ)"
              className="w-full text-lg font-bold text-slate-800 bg-transparent border-b border-transparent focus:border-emerald-400 focus:outline-none px-0 py-1 placeholder:text-slate-400 placeholder:font-normal"
            />
            {/* Hàng meta: char count + saving + embed badge */}
            <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1 text-slate-500">
                📄 <b className="text-slate-700">{(bodyMd || '').length.toLocaleString('vi-VN')}</b> ký tự
              </span>
              {saving && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1 text-amber-600 animate-pulse">
                    ⏳ Đang lưu…
                  </span>
                </>
              )}
              {embedModBusy && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="inline-flex items-center gap-1 text-sky-600 animate-pulse">
                    🔗 Đang embed…
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Action group bên phải */}
          <div className="shrink-0 flex items-center gap-1">
            {/* M104+M105 (2026-04-26): luôn hiện trạng thái chunks (cả khi 0) */}
            {(() => {
              const total = Number(m.chunks_count || 0);
              const embedded = Number(m.chunks_embedded || 0);
              if (total === 0) {
                // Chưa có chunks rời (chưa re-embed sau M104)
                return (
                  <span
                    className="text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-200"
                    title="Chưa chia chunks — bấm 🔄 hoặc Re-embed tất cả ở header"
                  >
                    Chưa chia chunks
                  </span>
                );
              }
              if (embedded === total) {
                return (
                  <span
                    className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200"
                    title={`${total} chunks đã embed đầy đủ`}
                  >
                    {total} chunks
                  </span>
                );
              }
              return (
                <span
                  className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200"
                  title={`${embedded}/${total} chunks đã embed — bấm 🔄 để retry`}
                >
                  {embedded}/{total} chunks
                </span>
              );
            })()}
            <EmbedBadge
              state={localModuleEmbedOk
                ? 'ok'
                : (m.embed_state || (m.has_embedding ? 'ok' : 'missing'))}
              onEmbed={embedModBusy ? null : async () => {
                setEmbedModBusy(true);
                try {
                  await api.adminRulesModuleEmbed(m.id);
                  toast.success(`Đã embed chương "${title}"`);
                  // M77: local override + refresh parent badge — KHÔNG onReload
                  setLocalModuleEmbedOk(true);
                  onAfterEmbed?.();
                } catch (e) {
                  toast.error('Embed chương lỗi: ' + (e?.data?.error || e?.message));
                } finally { setEmbedModBusy(false); }
              }}
            />
            <span className="text-slate-300 mx-1">|</span>
            <button onClick={() => onMove?.(-1)} disabled={index === 0}
                    title="Chương lên"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition">
              ▲
            </button>
            <button onClick={() => onMove?.(+1)} disabled={index === total - 1}
                    title="Chương xuống"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition">
              ▼
            </button>
            <button onClick={deleteChapter}
                    title="Xoá chương — không hoàn tác"
                    className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition">
              🗑
            </button>
          </div>
        </div>
      </div>

      {/* M67: collapse — chỉ render body khi expanded */}
      {!collapsed && (
        <>
          {/* Body rich text editor — bỏ label thừa "Nội dung chương" */}
          <div className="px-5 py-4">
            <RichTextEditor
              value={bodyMd}
              onChange={onBodyChange}
              placeholder={`Viết nội dung chương ${index + 1}... (có thể paste ảnh, dùng B/I, heading, list, bảng)`}
            />
            {/* M81 (2026-04-26): manual save — hiện nút khi có thay đổi */}
            {isDirty && (
              <div className="mt-3 flex items-center justify-end gap-2">
                <span className="text-xs text-amber-600 italic">⚠ Có thay đổi chưa lưu</span>
                <button
                  onClick={saveChanges}
                  disabled={saving}
                  className="px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm disabled:opacity-50"
                >
                  {saving ? '⏳ Đang lưu...' : '💾 Lưu thay đổi'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
      {collapsed && (
        <div className="px-5 py-3 text-center text-xs text-slate-400 bg-slate-50/50 italic">
          Chương đã thu gọn — bấm số {index + 1} ở header để mở lại
        </div>
      )}

    </section>
  );
}

// M81 (2026-04-26): Xoá AiGenQuestionsModal + DEFAULT_AI_HINT — M80 thay thế:
// NV bấm "Kiểm tra" → AI sinh on-demand. Admin không cần tạo Q&A trước nữa.

export default function AdminRulesModulesPage() {
  const user = useAuthStore(s => s.user);
  const [versionId, setVersionId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingChapter, setAddingChapter] = useState(false);
  // M57 (2026-04-25): Import from Word .docx
  const [importDocxOpen, setImportDocxOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [totpModalOpen, setTotpModalOpen] = useState(false);
  // M43: aggregate embed status + nút "Embed cả trang"
  const [embedMissing, setEmbedMissing] = useState({ modules: 0, questions: 0, total: 0 });
  const [embedAllBusy, setEmbedAllBusy] = useState(false);
  // M104 (2026-04-26): Re-embed all rules modules với TOTP
  const [reembedRulesBusy, setReembedRulesBusy] = useState(false);
  const [reembedRulesTotpOpen, setReembedRulesTotpOpen] = useState(false);

  if (!hasPermission(user, 'rules_modules')) {
    return <div className="p-6 text-red-600">403 — bạn không có quyền xem trang này.</div>;
  }

  async function loadVersions() {
    try {
      const r = await api.rulesVersions?.();
      const list = r?.versions || r || [];
      setVersions(list);
      const cur = list.find(v => v.is_current === 1) || list[0];
      if (cur && !versionId) {
        setVersionId(cur.id);
      } else if (list.length === 0) {
        // M79 (2026-04-26) — Defensive: nếu BE chưa apply auto-seed
        // (rules_versions trống), KHÔNG để spinner stuck mãi. Tắt loading
        // → render empty state "Chưa có chương nào" để admin biết trạng thái.
        // BE auto-seed lần restart kế là sẽ có version id=1.
        setLoading(false);
      }
    } catch {
      setVersions([{ id: 1, version_no: 1, is_current: 1 }]);
      if (!versionId) setVersionId(1);
    }
  }

  async function loadModules(vid) {
    if (!vid) return;
    setLoading(true);
    try {
      const r = await api.adminRulesModulesList(vid);
      setModules(r.modules || []);
    } catch (e) {
      toast.error('Load chương lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadVersions(); }, []);
  useEffect(() => { if (versionId) loadModules(versionId); }, [versionId]);

  // M43: load số missing embed mỗi khi modules đổi (để badge luôn tươi)
  async function loadEmbedMissing() {
    try {
      const r = await api.adminRulesEmbedMissingCount();
      setEmbedMissing(r);
    } catch { /* optional — không phá trang */ }
  }
  useEffect(() => { loadEmbedMissing(); }, [modules.length]);

  // M43: Embed cả trang — gọi retry endpoint, quét và embed tất cả missing/stale
  async function onEmbedAll() {
    if (embedAllBusy || embedMissing.total === 0) return;
    const ok = await showConfirm({
      title: 'Embed cả trang',
      message:
        `Sẽ embed ${embedMissing.modules} chương và ${embedMissing.questions} câu hỏi ` +
        `đang thiếu/lỗi thời.\n\n` +
        `Chi phí: vài trăm đồng tổng (OpenAI text-embedding-3-small).\n` +
        `Thời gian: ~5-30 giây tuỳ số lượng.\n\n` +
        `Tiếp tục?`,
      confirmText: 'Embed ngay',
    });
    if (!ok) return;
    setEmbedAllBusy(true);
    try {
      const r = await api.adminRulesEmbedRetryMissing();
      toast.success(
        `✅ Đã embed ${r.total_embedded} (${r.modules.embedded} chương + ${r.questions.embedded} câu). ` +
        (r.total_failed > 0 ? `⚠ ${r.total_failed} lỗi — thử lại sau.` : '')
      );
      // Reload data + missing count
      await loadModules(versionId);
      await loadEmbedMissing();
    } catch (e) {
      toast.error('Embed cả trang lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setEmbedAllBusy(false);
    }
  }

  // M104 (2026-04-26): Re-embed TẤT CẢ rules modules (re-chunk + re-embed mọi chương)
  async function onReembedAllRules() {
    if (reembedRulesBusy) return;
    const total = modules.length;
    if (total === 0) { toast('Chưa có chương nào để re-embed'); return; }
    const ok = await showConfirm({
      title: 'Re-embed tất cả chương nội quy',
      message: `Sẽ re-chunk + re-embed ${total} chương. Tốn 1 lần chi phí embed (~${(total * 0.5).toFixed(0)}-${(total * 2).toFixed(0)}k tokens × giá embedding-3-large). Tiếp tục?`,
      confirmText: 'Tiếp tục',
      dangerous: true,
    });
    if (!ok) return;
    setReembedRulesTotpOpen(true);
  }

  async function handleReembedAllRulesTotp(code) {
    setReembedRulesBusy(true);
    const t = toast.loading(`Đang re-embed ${modules.length} chương…`);
    try {
      const res = await api.adminRulesReembedAll({ adminTotp: code });
      toast.dismiss(t);
      if (res.failed > 0) {
        toast.error(
          `Re-embed: ${res.embedded}/${res.total} OK, ${res.failed} fail · ${res.cost_vnd ?? 0}đ`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `Đã re-embed ${res.embedded}/${res.total} chương (${res.cost_vnd ?? 0}đ)`,
          { duration: 6000 }
        );
      }
      setReembedRulesTotpOpen(false);
      // Reload modules để cập nhật chunks_count
      if (versionId) await loadModules(versionId);
      await loadEmbedMissing();
    } catch (err) {
      toast.dismiss(t);
      throw err; // Modal handle (TOTP sai)
    } finally {
      setReembedRulesBusy(false);
    }
  }

  async function addChapter() {
    if (addingChapter || !versionId) return;
    setAddingChapter(true);
    try {
      const newMod = await api.adminRulesModuleCreate(versionId, {
        title: `Chương ${modules.length + 1} — Chưa đặt tên`,
        body_md: '',
      });
      // Optimistic append — không reload toàn bộ → giữ nguyên scroll + state
      // các chương hiện có (tránh nhảy về đầu trang)
      setModules(prev => [...prev, { ...newMod, question_count: 0 }]);
      toast.success('Đã thêm chương');
      // Cuộn mượt đến chương vừa tạo
      setTimeout(() => {
        const el = document.getElementById(`chapter-${newMod.id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus vào input title để admin gõ ngay
        el?.querySelector('input[type="text"]')?.focus();
      }, 50);
    } catch (e) {
      toast.error('Thêm chương lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setAddingChapter(false);
    }
  }

  // M57 (2026-04-25): Import Word → split theo H1 → tạo nhiều chương 1 lúc
  async function importDocx(file) {
    if (!file) return;
    if (!versionId) { toast.error('Chưa có version hiện hành'); return; }
    if (!/\.docx$/i.test(file.name)) {
      toast.error('Chỉ chấp nhận file .docx');
      return;
    }
    setImporting(true);
    const t = toast.loading(`Đang xử lý ${file.name}…`);
    try {
      const res = await api.adminRulesModulesImportDocx(versionId, file);
      toast.dismiss(t);
      if (res.count === 0) {
        toast.error('File không có Heading 1 nào — không tạo được chương');
        return;
      }
      // M58: thêm info compress
      const stats = res.image_stats || {};
      const imgInfo = stats.embedded
        ? ` · 🖼 ${stats.embedded} ảnh${
            stats.compressed ? ` (${stats.compressed} đã nén, tiết kiệm ${stats.saved_mb} MB)` : ''
          }`
        : '';
      const warnInfo = res.warnings?.length
        ? ` · ⚠️ ${res.warnings.length} warning`
        : '';
      toast.success(
        `✓ Đã nhập ${res.count} chương${imgInfo}${warnInfo}. Tự động embed đang chạy ngầm.`,
        { duration: 6000 }
      );
      if (stats.skipped > 0) {
        toast(
          `⚠️ ${stats.skipped} ảnh bị bỏ qua (định dạng lạ như WMF/GIF, hoặc quá lớn ngay cả sau khi nén).`,
          { icon: '⚠️', duration: 6000 }
        );
      }
      if (res.warnings?.length) {
        for (const w of res.warnings.slice(0, 3)) toast(w, { icon: '⚠️' });
      }
      setImportDocxOpen(false);
      // Reload modules để hiển thị chương mới
      await loadModules(versionId);
    } catch (e) {
      toast.dismiss(t);
      toast.error('Nhập lỗi: ' + (e?.data?.error || e?.message || 'không rõ'));
    } finally {
      setImporting(false);
    }
  }

  // 2-layer confirm trước khi mở modal TOTP
  async function onClickDeleteAll() {
    if (modules.length === 0) {
      toast('Chưa có chương nào để xoá');
      return;
    }
    const confirm1 = await showConfirm({
      title: '⚠️ Xoá tất cả chương',
      message: `Bạn sắp XOÁ TẤT CẢ ${modules.length} chương của phiên bản này.\n\n` +
               `Toàn bộ câu hỏi + lịch ôn tập của NV với các chương này sẽ MẤT VĨNH VIỄN.\n\n` +
               `Chắc chắn tiếp tục?`,
      confirmText: 'Tiếp tục',
      dangerous: true,
    });
    if (!confirm1) return;
    setTotpModalOpen(true);
  }

  async function onSubmitTotp(code) {
    try {
      const r = await api.adminRulesModulesDeleteAll(versionId, code);
      toast.success(`Đã xoá ${r.deleted_count} chương`);
      setTotpModalOpen(false);
      setModules([]);  // optimistic clear
      await loadModules(versionId);
    } catch (e) {
      if (e?.data?.code === 'ADMIN_TOTP_INVALID') {
        throw e;  // modal sẽ reset input + báo "mã sai"
      }
      toast.error('Xoá thất bại: ' + (e?.data?.error || e?.message));
      setTotpModalOpen(false);
    }
  }

  async function moveChapter(idx, dir) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= modules.length) return;
    const reordered = [...modules];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setModules(reordered);
    try {
      await api.adminRulesModulesReorder(versionId, reordered.map(m => m.id));
    } catch {
      loadModules(versionId);
    }
  }

  // M147 (2026-05-12): Header layout 2-row chuẩn admin — back link trên, title 2xl, subtitle + actions dưới.
  const embedActions = (
    <>
      {embedMissing.total > 0 ? (
        <>
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold"
            title={`${embedMissing.modules} chương + ${embedMissing.questions} câu chưa hoặc cần embed lại`}
          >
            ⚠️ {embedMissing.total} cần embed
          </span>
          <button
            onClick={onEmbedAll}
            disabled={embedAllBusy}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-wait transition-all"
            title="Embed tất cả chương + câu hỏi đang thiếu hoặc lỗi thời"
          >
            {embedAllBusy ? (<>⏳ <span>Đang embed…</span></>) : (<>🧠 <span>Embed cả trang</span></>)}
          </button>
        </>
      ) : (
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold"
          title="Tất cả chương và câu hỏi đã được embed đầy đủ"
        >
          ✅ Đã embed đầy đủ
        </span>
      )}
      <button
        type="button"
        onClick={onReembedAllRules}
        disabled={reembedRulesBusy}
        title="Re-chunk + re-embed TẤT CẢ chương. Dùng khi vừa đổi chunk strategy hoặc setting."
        className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 disabled:opacity-50 disabled:cursor-wait"
      >
        {reembedRulesBusy ? '⏳ Đang re-embed…' : '🔄 Re-embed tất cả'}
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        <AdminPageHeader
          title="📚 Nội quy theo Chương"
          subtitle="Bấm 💾 Lưu sau khi sửa nội dung chương"
          actions={embedActions}
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6 pb-6 grid grid-cols-1 md:grid-cols-[1fr_300px] gap-4 md:gap-6 items-start">
        <main className="space-y-4 min-w-0">
        {loading && <div className="text-sm text-slate-500">Đang tải…</div>}

        {!loading && modules.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center">
            <div className="text-4xl mb-2">📝</div>
            <div className="text-slate-700 font-medium">Chưa có chương nào</div>
            <div className="text-xs text-slate-500 mt-1">
              Bấm "+ Thêm chương" để bắt đầu viết nội quy.
            </div>
          </div>
        )}

        {!loading && modules.map((m, i) => (
          <ChapterBlock
            key={m.id}
            m={m}
            index={i}
            total={modules.length}
            onReload={() => loadModules(versionId)}
            onMove={(dir) => moveChapter(i, dir)}
            onAfterEmbed={async () => {
              // M105 (2026-04-26): sau embed riêng module → reload modules để cập nhật chunks_count
              await loadEmbedMissing();
              if (versionId) await loadModules(versionId);
            }}
          />
        ))}

        {!loading && (
          <div className="space-y-2">
            <button
              onClick={addChapter}
              disabled={addingChapter}
              className="w-full py-4 bg-white border-2 border-dashed border-sky-300 hover:border-sky-500 hover:bg-sky-50 text-sky-700 rounded-2xl font-semibold transition disabled:opacity-50"
            >
              {addingChapter ? 'Đang thêm…' : '+ Thêm chương mới'}
            </button>
            <button
              onClick={() => setImportDocxOpen(true)}
              disabled={addingChapter || importing}
              className="w-full py-3 bg-white border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 rounded-2xl font-semibold transition disabled:opacity-50"
            >
              📥 Nhập nội quy từ Word (.docx) — tự tách chương theo <code className="font-mono bg-emerald-100 px-1 rounded">Heading 1</code>
            </button>
          </div>
        )}

        <div className="text-center text-xs text-slate-400 pt-4">
          💡 Admin gõ title + body bằng tay. Bấm 💾 Lưu khi sửa nội dung chương.
        </div>
        </main>

        {/* ============ TOC (mục lục) — cột phải ============ */}
        {!loading && modules.length > 0 && (
          <aside className="hidden md:block sticky top-[68px] self-start">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 max-h-[calc(100vh-100px)] overflow-y-auto">
              <div className="text-xs font-semibold text-slate-500 uppercase mb-2 px-1">
                📑 Mục lục ({modules.length})
              </div>
              <nav>
                <ol className="space-y-0.5">
                  {modules.map((m, i) => {
                    return (
                      <li key={m.id}>
                        <button
                          onClick={() => {
                            const el = document.getElementById(`chapter-${m.id}`);
                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-sky-50 text-slate-700 hover:text-sky-700 transition group"
                          title={m.title || '(chưa đặt tên)'}
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-mono text-slate-400 mt-0.5 group-hover:text-sky-500 flex-shrink-0">
                              {i + 1}.
                            </span>
                            {/* M82b (2026-04-26): bỏ truncate — hiện đầy đủ tên, wrap nhiều dòng */}
                            <span className="flex-1 break-words leading-snug">{m.title || '(chưa đặt tên)'}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </nav>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="mt-2 w-full text-xs text-slate-500 hover:text-slate-800 py-1 border-t border-slate-100 pt-2"
              >
                ↑ Lên đầu trang
              </button>
              {/* 2026-04-24: nút destructive — cần TOTP admin */}
              <button
                onClick={onClickDeleteAll}
                className="mt-2 w-full text-xs text-rose-600 hover:text-white hover:bg-rose-600 py-1.5 border border-rose-200 hover:border-rose-600 rounded transition"
                title="Xoá toàn bộ chương của phiên bản này (cần mã TOTP admin)"
              >
                🗑 Xoá tất cả chương
              </button>
            </div>
          </aside>
        )}
      </div>

      <AdminTotpReverifyModal
        open={totpModalOpen}
        title="🗑 Xoá tất cả chương nội quy"
        description={`Nhập mã 6 số từ Google Authenticator của admin để xác nhận xoá ${modules.length} chương.`}
        onClose={() => setTotpModalOpen(false)}
        onSubmit={onSubmitTotp}
      />

      {/* M104 (2026-04-26): TOTP modal cho Re-embed all rules modules */}
      <AdminTotpReverifyModal
        open={reembedRulesTotpOpen}
        title="🔄 Re-embed tất cả chương"
        description={`Nhập mã 6 số TOTP để xác nhận re-chunk + re-embed ${modules.length} chương. Tốn chi phí embed.`}
        onClose={() => setReembedRulesTotpOpen(false)}
        onSubmit={handleReembedAllRulesTotp}
      />

      {importDocxOpen && (
        <ImportDocxModal
          onClose={() => setImportDocxOpen(false)}
          onSubmit={importDocx}
          importing={importing}
        />
      )}
    </div>
  );
}

// =====================================================================
// M57 (2026-04-25): Modal upload Word để tự tách chương theo H1
// =====================================================================
function ImportDocxModal({ onClose, onSubmit, importing }) {
  const [file, setFile] = useState(null);
  function handleSubmit(e) {
    e?.preventDefault();
    if (!file) return;
    onSubmit(file);
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
         onClick={() => !importing && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl">📥</div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-slate-800">Nhập nội quy từ Word</h2>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Upload file <b>.docx</b> — app sẽ tự tách thành các chương theo <b>Heading 1</b>.
              Mỗi Heading 1 = 1 chương. Ảnh và định dạng được giữ nguyên.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              File Word (.docx) — tối đa 25 MB
            </label>
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={importing}
              className="w-full text-sm border border-slate-300 rounded-lg p-2 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-emerald-50 file:text-emerald-800 file:font-semibold hover:file:bg-emerald-100"
            />
            {file && (
              <div className="mt-2 text-xs text-slate-600">
                ✓ Đã chọn: <b>{file.name}</b> ({(file.size / 1024).toFixed(0)} KB)
              </div>
            )}
          </div>

          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 leading-relaxed">
            <div className="font-semibold mb-1">💡 Yêu cầu file Word:</div>
            <ul className="list-disc ml-4 space-y-0.5">
              <li>Mỗi tên chương phải dùng <b>style Heading 1</b> (Ctrl+Alt+1 trong Word)</li>
              <li>Sub-mục dùng Heading 2, Heading 3 (sẽ giữ trong body chương)</li>
              <li>Các chương hiện tại <b>không bị xoá</b> — chương mới thêm vào cuối</li>
              <li>✨ Ảnh lớn sẽ được <b>tự động nén</b> (resize + JPEG optimize)</li>
              <li>Tối đa 30 ảnh/file, tổng 18 MB sau khi nén</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 text-sm"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={!file || importing}
              className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed text-white text-sm font-bold shadow transition"
            >
              {importing ? '⏳ Đang xử lý…' : '📥 Nhập + Tách chương'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
